import { Card, AnalysisResult, GameSettings, InsuranceAnalysisResult } from '../types';
import { getBasicStrategyDecision } from './basicStrategy';
import { getHandValue } from './deck';

// Sentinel true count meaning "deviations are turned off" on the legacy numeric path.
export const DEVIATIONS_DISABLED_SENTINEL = -999;

// Define deviations (Illustrious 18 subset relevant to Hit/Stand/Double/Surrender)
// Format: Condition function -> Best Action + Reason
type DeviationCheck = (
    playerValue: number,
    dealerValue: number,
    trueCount: number,
    isSoft: boolean,
    canSurrender: boolean
) => { action: 'hit' | 'stand' | 'double' | 'surrender' | 'split'; deviationName: string } | null;

const deviations: DeviationCheck[] = [
    // 16 vs 10: Stand if TC >= 0
    (pv, dv, tc, soft) => {
        if (!soft && pv === 16 && dv === 10 && tc >= 0) {
            return { action: 'stand', deviationName: 'Illustrious 18: Stand on 16 vs 10 when TC >= 0' };
        }
        return null;
    },
    // 15 vs 10: Stand if TC >= 4
    (pv, dv, tc, soft) => {
        if (!soft && pv === 15 && dv === 10 && tc >= 4) {
            return { action: 'stand', deviationName: 'Illustrious 18: Stand on 15 vs 10 when TC >= 4' };
        }
        return null;
    },
    // 12 vs 3: Stand if TC >= 2
    (pv, dv, tc, soft) => {
        if (!soft && pv === 12 && dv === 3 && tc >= 2) {
            return { action: 'stand', deviationName: 'Illustrious 18: Stand on 12 vs 3 when TC >= 2' };
        }
        return null;
    },
    // 12 vs 2: Stand if TC >= 3
    (pv, dv, tc, soft) => {
        if (!soft && pv === 12 && dv === 2 && tc >= 3) {
            return { action: 'stand', deviationName: 'Illustrious 18: Stand on 12 vs 2 when TC >= 3' };
        }
        return null;
    },
    // 10 vs 10: Double if TC >= 4
    (pv, dv, tc, soft) => {
        if (!soft && pv === 10 && dv === 10 && tc >= 4) {
            return { action: 'double', deviationName: 'Illustrious 18: Double on 10 vs 10 when TC >= 4' };
        }
        return null;
    },
    // 11 vs Ace: Double if TC >= 1
    (pv, dv, tc, soft) => {
        if (!soft && pv === 11 && dv === 11 && tc >= 1) {
            return { action: 'double', deviationName: 'Illustrious 18: Double on 11 vs Ace when TC >= 1' };
        }
        return null;
    },
    // 10 vs A: Double if TC >= 4
    (pv, dv, tc, soft) => {
        if (!soft && pv === 10 && dv === 11 && tc >= 4) {
            return { action: 'double', deviationName: 'Illustrious 18: Double on 10 vs Ace when TC >= 4' };
        }
        return null;
    },
    // 9 vs 2: Double if TC >= 1
    (pv, dv, tc, soft) => {
        if (!soft && pv === 9 && dv === 2 && tc >= 1) {
            return { action: 'double', deviationName: 'Illustrious 18: Double on 9 vs 2 when TC >= 1' };
        }
        return null;
    },
    // 9 vs 7: Double if TC >= 3
    (pv, dv, tc, soft) => {
        if (!soft && pv === 9 && dv === 7 && tc >= 3) {
            return { action: 'double', deviationName: 'Illustrious 18: Double on 9 vs 7 when TC >= 3' };
        }
        return null;
    },
    // 16 vs 9: Stand if TC >= 5
    (pv, dv, tc, soft) => {
        if (!soft && pv === 16 && dv === 9 && tc >= 5) {
            return { action: 'stand', deviationName: 'Illustrious 18: Stand on 16 vs 9 when TC >= 5' };
        }
        return null;
    },
    // 13 vs 2: Hit if TC <= -1
    (pv, dv, tc, soft) => {
        if (!soft && pv === 13 && dv === 2 && tc <= -1) {
            return { action: 'hit', deviationName: 'Illustrious 18: Hit on 13 vs 2 when TC <= -1' };
        }
        return null;
    },
    // 12 vs 4: Hit if TC <= 0
    (pv, dv, tc, soft) => {
        if (!soft && pv === 12 && dv === 4 && tc <= 0) {
            return { action: 'hit', deviationName: 'Illustrious 18: Hit on 12 vs 4 when TC <= 0' };
        }
        return null;
    },
    // 12 vs 5: Hit if TC <= -2
    (pv, dv, tc, soft) => {
        if (!soft && pv === 12 && dv === 5 && tc <= -2) {
            return { action: 'hit', deviationName: 'Illustrious 18: Hit on 12 vs 5 when TC <= -2' };
        }
        return null;
    },
    // 12 vs 6: Hit if TC <= -1
    (pv, dv, tc, soft) => {
        if (!soft && pv === 12 && dv === 6 && tc <= -1) {
            return { action: 'hit', deviationName: 'Illustrious 18: Hit on 12 vs 6 when TC <= -1' };
        }
        return null;
    },
    // 13 vs 3: Hit if TC <= -2
    (pv, dv, tc, soft) => {
        if (!soft && pv === 13 && dv === 3 && tc <= -2) {
            return { action: 'hit', deviationName: 'Illustrious 18: Hit on 13 vs 3 when TC <= -2' };
        }
        return null;
    }
];

function getCardNumericValue(card: Card): number {
    if (card.rank === 'A') return 11;
    if (['J', 'Q', 'K'].includes(card.rank)) return 10;
    return parseInt(card.rank);
}

/**
 * Analyze a player's decision against basic strategy and deviations.
 *
 * Two calling conventions are supported:
 * 1. Pass a `GameSettings` object as the 4th arg plus the real true count as the optional 5th arg
 *    `trueCountArg` (the convention used by gameStore.ts). `settings.useDeviations` gates whether
 *    deviations are checked; `trueCountArg` supplies the true count (defaults to 0 when omitted).
 * 2. Legacy: pass a raw `number` true count as the 4th arg. Deviations are checked unless the
 *    value is the `-999` sentinel, which disables them. Retained for tests/back-compat.
 *
 * @param playerCards - The player's cards
 * @param dealerUpCard - The dealer's up card
 * @param playerAction - The action the player took
 * @param settingsOrTrueCount - Either GameSettings object or true count number (for backward compatibility)
 * @param trueCountArg - The real true count; only used when a GameSettings object is passed
 */
export function analyzeDecision(
    playerCards: Card[],
    dealerUpCard: Card,
    playerAction: 'hit' | 'stand' | 'double' | 'surrender' | 'split',
    settingsOrTrueCount: GameSettings | number,
    trueCountArg?: number,
): AnalysisResult {
    const { value, soft } = getHandValue(playerCards);
    const dealerValue = getCardNumericValue(dealerUpCard);

    // Handle both GameSettings and direct trueCount for backward compatibility
    let trueCount: number;
    let useDeviations: boolean;

    if (typeof settingsOrTrueCount === 'number') {
        trueCount = settingsOrTrueCount;
        // Legacy numeric path. The -999 sentinel means "deviations disabled": it must NOT
        // enable the deviation loop, because several deviations fire on `tc <= X` conditions
        // (e.g. 12 vs 4 hit at TC <= 0) and would wrongly trigger at -999.
        useDeviations = trueCount !== DEVIATIONS_DISABLED_SENTINEL;
    } else {
        // GameSettings path: use trueCountArg if supplied, otherwise default to 0
        trueCount = trueCountArg ?? 0;
        useDeviations = settingsOrTrueCount.useDeviations ?? false;
    }

    // 1. Get Basic Strategy Move
    const basicStrategyAction = getBasicStrategyDecision(playerCards, dealerUpCard);

    // 2. Check for Deviations (only if enabled)
    let bestAction = basicStrategyAction;
    let deviationName: string | undefined;

    if (useDeviations) {
        for (const check of deviations) {
            const deviation = check(value, dealerValue, trueCount, soft, true);
            if (deviation) {
                bestAction = deviation.action;
                deviationName = deviation.deviationName;
                break;
            }
        }
    }

    // 3. Compare Player Action
    const isCorrect = playerAction === bestAction;

    // 4. Generate Reason
    let reason = '';
    if (isCorrect) {
        if (deviationName) {
            reason = `Correct! ${deviationName}.`;
        } else {
            reason = `Correct! Basic Strategy says to ${bestAction}.`;
        }
    } else {
        if (deviationName) {
            reason = `Incorrect. ${deviationName}.`;
        } else {
            reason = `Incorrect. Basic Strategy says to ${bestAction}.`;
        }
    }

    return {
        isCorrect,
        playerAction,
        bestAction,
        reason,
        basicStrategyAction,
        deviationApplied: deviationName
    };
}

/**
 * Analyze whether taking or declining insurance was correct based on true count.
 *
 * Standard rule (6-deck): take insurance at TC >= +3.
 * Insurance pays 2:1; break-even when P(dealer BJ) = 1/3.
 *
 * `bestAction` is driven directly from the threshold so the trainer is
 * internally consistent. The EV figure (percentage points) is explanatory
 * color only — calibrated so the zero-crossing is near TC +3.
 *
 * `expectedValue` is now a real percentage (e.g. -7.69 at TC 0, ~+0.13 at TC +3).
 */
export function analyzeInsuranceDecision(
    playerAction: 'take' | 'decline',
    trueCount: number
): InsuranceAnalysisResult {
    const INSURANCE_THRESHOLD = 3; // 6-deck standard

    // EV of insurance per unit = 3*p - 1 where p = P(dealer has BJ)
    // Approximate p vs TC for display only (calibrated so EV crosses 0 near TC +2.5..+3):
    const p = 0.3077 + trueCount * 0.0096;
    const evPercent = (3 * p - 1) * 100; // percentage points

    const bestAction: 'take' | 'decline' = trueCount >= INSURANCE_THRESHOLD ? 'take' : 'decline';
    const isCorrect = playerAction === bestAction;

    const explanation = bestAction === 'take'
        ? `Take insurance at TC +${trueCount}: at/above +${INSURANCE_THRESHOLD} it has positive EV (≈ ${evPercent.toFixed(1)}%).`
        : `Decline at TC ${trueCount >= 0 ? '+' : ''}${trueCount}: below +${INSURANCE_THRESHOLD} insurance is -EV (≈ ${evPercent.toFixed(1)}%).`;

    return {
        isCorrect,
        bestAction,
        explanation: isCorrect ? explanation : `Incorrect. ${explanation}`,
        expectedValue: Math.round(evPercent * 100) / 100, // real percentage, e.g. -7.69 at TC 0
    };
}
